/**
 * "What do I get for finishing this course, and have I got it yet?"
 *
 * ONE COMPONENT, TWO PLACES: the course detail page and the end of the courseware player.
 * A learner asks the question in both, and answering it twice would be two chances to get
 * "earned" wrong in different ways.
 *
 * THIS IS THE COURSE CERTIFICATE. It is a different record, from a different table, written
 * by a different trigger, from the LEARNING PATH certificate on `PathDetail`. A learner may
 * hold both; neither one's state is ever derived from the other's, and this component knows
 * nothing about pathways.
 *
 * WHAT DECIDES "EARNED" — and what deliberately does not
 * ------------------------------------------------------
 * The certificate RECORD decides, never a progress percentage. Content completion and
 * having earned a certificate are different questions, and on this deployment they disagree
 * permanently in BOTH directions:
 *
 *   100% complete, no certificate   PATHTEST Course A reads `complete_count: 1,
 *                                   incomplete_count: 0` with a course grade of 0.02 and no
 *                                   certificate configuration. Forever.
 *
 *   certificate held, "not earned"  and this is the one that cost a rebuild. The obvious
 *                                   source is `certificate_data` on
 *                                   `/api/course_home/v1/progress/{course}/`. It is WRONG
 *                                   HERE. Upstream's `get_cert_data` runs
 *                                   `can_show_certificate_message`, which requires
 *                                   `course_grade.passed` — and thinlms issues certificates
 *                                   on COMPLETION, not on passing a grade. Measured against
 *                                   the fixture learner: two `GeneratedCertificate` rows,
 *                                   both `downloadable`, both listed by
 *                                   `/api/certificates/v0/`, and `certificate_data: null`
 *                                   for both. A section built on it showed "not earned" to
 *                                   somebody holding the certificate.
 *
 * So this reads `/api/certificates/v0/certificates/<username>/` — the same list
 * `MyCertificates` renders, already filtered by the platform to passing status, viewable
 * display behaviour and an active configuration — and the dashboard's `certificate.isEarned`
 * for the one state that list cannot express: earned but not yet released.
 *
 * THREE CONDITIONS BEFORE ANYTHING RENDERS, and each removes a way of lying:
 *
 *   1. the learner is ENROLLED — a visitor browsing a course is not owed a status for it;
 *   2. the course HAS an active certificate configuration (`awardsCertificate`, answered by
 *      our own about endpoint because the platform's progress payload cannot distinguish
 *      "no certificate here" from "not earned yet");
 *   3. the two reads have actually answered — a failure says so rather than guessing.
 *
 * Fail any of them and this renders NOTHING. A page with no certificate section is a page
 * that has not promised anything.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banner, Icon } from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import { getCourseAbout, getMyCourses } from '../../shared/lib/catalogue';
import { loadSession } from '../../shared/lib/session';
import { listEarnedCertificates } from '../../shared/lib/certificates';
import './course-certificate.css';

/** Where a course certificate is rendered. A route in this app, not a proxied LMS page. */
const certificatePath = (uuid: string) => `/certificates/${uuid}`;

export function CourseCertificateSection({
  courseId,
  enrolled,
  /** `full` on the course page; `compact` at the end of the player, where it is an aside. */
  variant = 'full',
}: {
  courseId: string;
  enrolled: boolean;
  variant?: 'full' | 'compact';
}) {
  const academy = getConfig().tenant.name;

  // The same query key the course page already uses, so on that page this costs nothing:
  // react-query serves the cached record rather than making a second request.
  const about = useQuery({
    queryKey: ['course-about', courseId],
    queryFn: () => getCourseAbout(courseId),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const username = session.data?.user?.username ?? null;

  // The SAME query key `MyCertificates` uses, so a learner moving between the two pages
  // makes one request, and the two surfaces can never disagree about what they hold.
  const certificate = useQuery({
    queryKey: ['my-certificates', username],
    queryFn: () => listEarnedCertificates(username as string),
    enabled: enrolled && Boolean(username),
    retry: false,
  });

  // Computed HERE, above the dashboard read, rather than below the early returns — because
  // it is what decides whether that read is needed at all. Hooks run before any `return`,
  // so a condition expressed only after one cannot gate a query.
  const held = (certificate.data ?? []).find((entry) => entry.course_id === courseId) ?? null;
  const earned = Boolean(held?.uuid);

  /*
   * The dashboard, for the one state the list above cannot express. A certificate that is
   * earned but held back is ABSENT from that list rather than listed as pending — so
   * `isEarned` here is the only place "you have it, the academy has not released it" lives.
   *
   * `enabled` NOW REPEATS EVERY CONDITION UNDER WHICH THIS COMPONENT RENDERS ANYTHING, and
   * that is the fix. It used to be `enabled: enrolled` alone, so `GET /api/learner_home/init`
   * — every enrolment, grade and certificate the learner holds across every course on every
   * academy — was fetched to answer one boolean, and fetched in two cases where the answer
   * was never read:
   *
   *   the certificate is already HELD   `heldBack` is `!earned && …`. With `earned` true the
   *                                     component returns the reward banner and never looks
   *                                     at `home.data`. The fetch was pure waste.
   *   the course awards NOTHING         hooks run before the `awardsCertificate !== true`
   *                                     return below, so every course on the academy with no
   *                                     certificate configuration paid for it too.
   *
   * NOT FIXED BY ADDING A FLAG TO `/api/tenant/v1/courses/{id}/about`, which is the obvious
   * backend answer and the wrong one: that response is cached shared and version-keyed across
   * users, and a per-user boolean in it makes the cache either wrong or worthless.
   *
   * `['my-courses']` is SHARED with Dashboard, MyCertificates, Profile and the player — see
   * `Unit.tsx`, which invalidates this key on unit unmount because finishing the last unit
   * can issue the certificate. That is the one call site whose behaviour this `enabled`
   * changes: after the invalidation the compact section refetches only while it still needs
   * the answer. So this only saves
   * a real request on a cold course-page load — where it saves the whole thing. MEASURED on
   * the shared dev deployment, 2026-08-18, one cold load per case, that payload being ~6.9 KB
   * for a learner with four enrolments: a course whose certificate the learner holds went
   * from 1 request to 0, a course that awards no certificate went from 1 to 0, and a course
   * that awards one the learner has not earned stayed at 1 — which is correct, and is the
   * same `enabled` state the withheld case is in.
   */
  const home = useQuery({
    queryKey: ['my-courses'],
    queryFn: getMyCourses,
    enabled:
      enrolled &&
      about.data?.awardsCertificate === true &&
      // A pending list includes a DISABLED one in React Query v5 (no username yet), which is
      // also a state this component renders nothing in.
      !certificate.isPending &&
      // An error renders the "we could not check" block, which reads neither answer.
      !certificate.isError &&
      !earned,
    retry: false,
  });

  // Condition 1 and 2. Both are "render nothing", not "render a spinner": this is an
  // additive block on somebody else's page, and a placeholder for a section that may never
  // appear is worse than its absence.
  if (!enrolled) return null;
  if (about.data?.awardsCertificate !== true) return null;
  if (certificate.isPending) return null;

  /*
   * AND THE DASHBOARD READ, WHEN THERE IS ONE IN FLIGHT.
   *
   * `heldBack` below reads `home.data`, and `(undefined ?? []).some(...)` is `false` — which
   * is not "not held back", it is "not answered yet". Rendering on it prints
   * "Finish this course to earn a certificate" to somebody who HAS earned it and is waiting
   * for the academy to release it, and then flips to the opposite sentence when the answer
   * lands. That was a race before the query above was gated; gating it made the race
   * unwinnable, because `home` can no longer start until the certificate list has settled.
   * So the wrong sentence is not raced any more — it is waited out.
   *
   * `isPending && fetchStatus !== 'idle'` is the discriminator, and the pair is required. In
   * React Query v5 a DISABLED query is also `isPending`, with `fetchStatus: 'idle'` — and
   * disabled is exactly the state `home` is in whenever its answer is not needed (the
   * learner already holds the certificate, the course awards none, the certificate list
   * failed). Testing `isPending` alone would withhold the reward banner forever.
   *
   * `!earned` LEADS, AND EVERY GATE BELOW THAT READS `home` REPEATS IT. The rule this is
   * obeying: a render gate on `home` must never be WIDER than the `enabled` that fetches it.
   * `['my-courses']` is a shared cache entry, and an observer reports the QUERY's status, not
   * its own — a disabled observer over an errored query still reads `isError: true`. So a
   * gate that asks about `home` in a state where this component deliberately does not fetch
   * it is asking about somebody else's request. See the note on the failure branch below.
   */
  if (!earned && home.isPending && home.fetchStatus !== 'idle') return null;

  const heldBack =
    !earned &&
    (home.data ?? []).some(
      (entry) => entry.courseRun.courseId === courseId && entry.certificate.isEarned,
    );

  const Wrapper = variant === 'compact' ? CompactShell : FullShell;

  /*
   * EITHER READ FAILING LANDS HERE — but `home` only counts when this component actually
   * WANTED it, which is what `!earned` says.
   *
   * The `home` half is the fix to a hole this component already had: it is fetched with
   * `retry: false`, so one failure is final, and because `heldBack` is computed from
   * `home.data` a silent failure told a learner whose certificate is WITHHELD that they had
   * not earned it, permanently, with no way back short of a reload.
   *
   * THE `!earned` IS NOT DECORATION, and leaving it off was a worse bug than the one being
   * fixed. `['my-courses']` is a SHARED key and React Query reports the QUERY's status to
   * every observer of it — `enabled: false` stops this observer FETCHING, it does not stop it
   * INHERITING an `error` status somebody else's fetch wrote. So: a learner opens the
   * dashboard, its `learner_home/init` fails, the shared entry settles into `status: 'error'`.
   * They then navigate — client-side, no reload — to a course whose certificate they hold and
   * have been issued. `earned` is true, so this observer is disabled, so it never refetches
   * and never clears the error; it just reads it. Without `!earned` this branch matched, and
   * somebody holding a released certificate was told "we could not check" and lost the View
   * and Download links below, for the five minutes the poisoned entry survives in cache,
   * because of a request that failed on a different screen.
   *
   * The learner this branch exists for has `earned === false` by definition, so scoping it
   * costs that learner nothing. "We could not check" stays the honest answer to a question we
   * could not answer, and stops being an answer to a question we never asked.
   */
  if (certificate.isError || (!earned && home.isError)) {
    return (
      <Wrapper>
        <div className="course-cert" data-state="unknown">
          <span className="course-cert__seal" aria-hidden="true">
            <Icon name="workspace_premium" />
          </span>
          <div className="course-cert__body">
            <h3>Course certificate</h3>
            <p>
              We could not check your certificate for this course just now. Nothing has been
              lost — reload the page to try again.
            </p>
          </div>
        </div>
      </Wrapper>
    );
  }

  // THE REWARD MOMENT — a banner, not a quiet row.
  //
  // The prototype has no certificate banner on either of these screens; checked, rather
  // than assumed. What it DOES have is a green success strip on the My Learning card,
  // `check_circle` + "Congratulations. Your certificate is ready." + a View action
  // (`index.html:5156`), and this reuses that copy and that affordance in this app's own
  // `Banner tone="success"` — the same component MyCertificates already uses to announce
  // certificate news. One visual language, not a second one invented here.
  if (earned && held?.uuid) {
    return (
      <Wrapper>
        <div className="course-cert-banner">
          <Banner tone="success">
            <span className="course-cert-banner__row">
              <span className="course-cert-banner__seal" aria-hidden="true">
                <Icon name="workspace_premium" />
              </span>
              <span className="course-cert-banner__text">
                <strong>Congratulations. Your certificate is ready.</strong> Issued by{' '}
                {academy}. Anyone with its link can check it — no sign-in needed.
              </span>
              <span className="course-cert-banner__actions">
                <Link
                  to={certificatePath(held.uuid)}
                  className="ui-btn"
                  data-variant="primary"
                >
                  <Icon name="workspace_premium" />
                  View certificate
                </Link>
                {/*
                  A REAL DOWNLOAD, not a label. It opens the certificate page with
                  `?print=1`, and that page calls `window.print()` once the document has
                  rendered — the same mechanism as the Download PDF button already on it,
                  and the only one this product has (there is no PDF pipeline anywhere).
                  Printing from HERE would print the course page, which is why it navigates
                  first rather than firing the dialog in place.
                */}
                <Link
                  to={`${certificatePath(held.uuid)}?print=1`}
                  className="ui-btn"
                  data-variant="secondary"
                >
                  <Icon name="download" />
                  Download PDF
                </Link>
              </span>
            </span>
          </Banner>
        </div>
      </Wrapper>
    );
  }

  if (heldBack) {
    return (
      <Wrapper>
        <div className="course-cert" data-state="held">
          <span className="course-cert__seal" aria-hidden="true">
            <Icon name="schedule" />
          </span>
          <div className="course-cert__body">
            <h3>Course certificate</h3>
            <p>
              <strong>You have earned this certificate.</strong> {academy} releases it
              {' '}when the course ends, and it will appear here then.
            </p>
          </div>
        </div>
      </Wrapper>
    );
  }

  // Not earned. Says what it takes, and states the grade condition only as a possibility,
  // because this component cannot see whether the course grades anything — and a flat "you
  // need a passing grade" on an ungraded course is a requirement that does not exist.
  return (
    <Wrapper>
      <div className="course-cert" data-state="not_earned">
        <span className="course-cert__seal" aria-hidden="true">
          <Icon name="workspace_premium" />
        </span>
        <div className="course-cert__body">
          <h3>Course certificate</h3>
          <p>
            Finish this course to earn a certificate from {academy}. If the course grades
            you, you need a passing grade as well. It appears here as soon as it is issued.
          </p>
        </div>
      </div>
    </Wrapper>
  );
}

/** On the course page it is one of the page's own sections, with a heading above it. */
function FullShell({ children }: { children: React.ReactNode }) {
  return <section className="course__section">{children}</section>;
}

/**
 * At the end of the player it is not a section of the course page.
 *
 * No heading of its own and no card chrome around a card: it sits between the last unit and
 * the navigation, where the learner has just run out of course, and it must read as the end
 * of the journey rather than as another thing to do.
 */
function CompactShell({ children }: { children: React.ReactNode }) {
  return <div className="player__cert">{children}</div>;
}
